<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">位次匹配</h1>
    </div>
    <div class="page-content">
      <!-- 查询表单卡片 -->
      <div class="query-card">
        <h2 class="card-title">查询条件</h2>
        
        <!-- 横向排列的表单内容 -->
        <div class="form-row">
          <!-- 分数输入 -->
          <div class="form-group-horizontal">
            <label class="form-label">高考分数</label>
            <div class="score-input-wrapper">
              <input
                v-model.number="score"
                type="number"
                placeholder="例如：600"
                class="score-input"
                :min="0"
                :max="750"
              />
              <span class="unit-label">分</span>
            </div>
          </div>

          <!-- 年份选择 -->
          <div class="form-group-horizontal">
            <label class="form-label">年份</label>
            <div class="year-select-wrapper">
              <el-select
                v-model="selectedYear" 
                class="year-select"
                placeholder="选择年份"
              >
                <el-option label="2024年" value="2024"></el-option>
                <el-option label="2025年" value="2025"></el-option>
                <el-option label="2026年" value="2026"></el-option>
              </el-select>
            </div>
          </div>

          <!-- 选科组合 -->
          <div class="form-group-horizontal">
            <label class="form-label">选科组合</label>
            <div class="subject-select-wrapper">
              <el-select
                v-model="category" 
                class="subject-select"
                placeholder="选择选科组合"
              >
                <el-option label="物理类" value="physics"></el-option>
                <el-option label="历史类" value="history"></el-option>
              </el-select>
            </div>
          </div>

          <!-- 查询按钮 -->
          <el-button type="success" class="query-btn" @click="handleQuery" :loading="isLoading">
            查询推荐院校
          </el-button>
        </div>
      </div>

      <!-- 查询结果展示 -->
      <div v-if="showResult" class="result-card">
        <h2 class="card-title result-title">
          {{ selectedYear }}年高考位次查询结果
        </h2>

        <!-- 分数和位次横向排列 -->
        <div class="result-row">
          <!-- 用户分数卡片 -->
          <div class="result-item score-card">
            <div class="result-icon">📝</div>
            <div class="result-content">
              <div class="result-label">您的分数</div>
              <div class="result-value">{{ score }} <span class="unit">分</span></div>
            </div>
          </div>

          <!-- 全省位次卡片 -->
          <div class="result-item rank-card">
            <div class="result-icon">🏆</div>
            <div class="result-content rank-content">
              <div class="rank-left">
                <div class="result-label">全省位次</div>
                <div class="rank-value-wrapper">
                  <span class="result-value highlight">{{ result.rank || '--' }}</span>
                  <span class="result-unit">名</span>
                </div>
              </div>
              <div class="rank-right">
                <div class="same-score-box">
                  <span class="same-score-label">同分人数</span>
                  <span class="same-score-value">{{ result.samescore || 0 }} 人</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 推荐学校列表（包含匹配结果统计） -->
      <div v-if="showResult" class="schools-card">
        <!-- 匹配结果统计 -->
        <div class="stats-header">
          <div class="stats-card">
            <div class="result-icon">📊</div>
            <div class="result-content">
              <div class="result-label">匹配结果统计</div>
              <div class="result-value">{{ result.schoolCount || 0 }} <span class="unit">所院校</span></div>
              <div class="result-sub" v-if="result.isMaxScore">⚠️ 分数达到最高分，位次参考价值有限</div>
            </div>
          </div>
        </div>
        
        <div class="schools-list">
          <div 
            v-for="group in paginatedSchools" 
            :key="group.schoolId" 
            class="school-card"
          >
            <div class="school-logo">
              <img 
                :src="getLogoUrl(group.logo)" 
                :alt="group.schoolName"
                class="logo-img"
                @error="handleImageError($event)"
              />
            </div>
            <div class="school-info">
              <h3 class="school-name">{{ group.schoolName }}</h3>
              
              <!-- 选科组合标签 -->
              <div class="discipline-tags">
                <span 
                  v-for="(item, index) in group.disciplines" 
                  :key="index"
                  class="discipline-tag"
                  :class="{ active: group.selectedDiscipline === index }"
                  @click="selectDiscipline(group, index)"
                >
                  {{ item.disciplineType || '暂无选科要求' }}
                </span>
              </div>
              
              <div class="school-details">
                <!-- 分数区间 -->
                <div class="detail-item">
                  <span class="detail-label">分数区间</span>
                  <span class="detail-value">
                    {{ formatScore(currentDiscipline(group.disciplines, group.selectedDiscipline).minScore) }} - {{ formatScore(currentDiscipline(group.disciplines, group.selectedDiscipline).maxScore) }}分
                  </span>
                </div>
                
                <!-- 位次范围 -->
                <div class="detail-item">
                  <span class="detail-label">位次范围</span>
                  <span class="detail-value">{{ currentDiscipline(group.disciplines, group.selectedDiscipline).rankRange || '--' }}</span>
                </div>
                
                <!-- 最高分位次 -->
                <div class="detail-item">
                  <span class="detail-label">最高分位次</span>
                  <span class="detail-value highlight">{{ formatNumber(currentDiscipline(group.disciplines, group.selectedDiscipline).maxScoreRank) }}名</span>
                </div>
                
                <!-- 最低分位次 -->
                <div class="detail-item">
                  <span class="detail-label">最低分位次</span>
                  <span class="detail-value highlight">{{ formatNumber(currentDiscipline(group.disciplines, group.selectedDiscipline).minScoreRank) }}名</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页组件 -->
        <div v-if="totalPages > 1" class="pagination-container">
          <el-pagination
            :current-page="currentPage"
            :page-size="pageSize"
            :total="groupedSchools.length"
            layout="prev, pager, next"
            @current-change="handlePageChange"
            background
          />
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="showResult && recommendedSchools.length === 0" class="empty-schools">
        <div class="empty-icon">📭</div>
        <p>暂无匹配的推荐院校</p>
        <p class="empty-hint">请尝试调整分数范围</p>
      </div>

      <!-- 使用说明 -->
      <div class="info-card">
        <h3 class="info-title">📌 使用说明</h3>
        <ul class="info-list">
          <li>
            <span class="info-icon">📊</span>
            <span><strong>数据来源：</strong>河北省教育考试院一分一段表</span>
          </li>
          <li>
            <span class="info-icon">⚠️</span>
            <span><strong>仅供参考：</strong>查询结果仅供参考，不作为正式录取依据</span>
          </li>
          <li>
            <span class="info-icon">💡</span>
            <span><strong>志愿填报：</strong>建议结合历年录取分数线和专业排名进行综合分析</span>
          </li>
          <li>
            <span class="info-icon">🔍</span>
            <span><strong>数据说明：</strong>部分院校可能存在分数/位次为空的情况，建议核实最新招生信息</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive } from "vue";
import { getRankQuery } from "@/api/processApi";
import { baseURL } from "@/api/axiosConfig";

export default {
  name: "RankQuery",
  components: {},
  data() {
    return {
      score: 600,
      year: 2025,
      selectedYear: '2025',
      category: 'physics',
      showResult: false,
      result: {
        rank: 0,
        samescore: 0,
        schoolCount: 0,
        isMaxScore: false,
        total: null
      },
      recommendedSchools: [],
      isLoading: false,
      searchKeyword: '',
      selectedDisciplines: reactive({}), // 存储每个学校选中的选科索引 { schoolId: selectedIndex }
      currentPage: 1, // 当前页码
      pageSize: 5 // 每页显示数量
    };
  },
  computed: {
    // 筛选院校列表（用于搜索）
    filteredSchools() {
      if (!this.searchKeyword) return this.recommendedSchools;
      const keyword = this.searchKeyword.toLowerCase();
      return this.recommendedSchools.filter(school => 
        school.schoolName.toLowerCase().includes(keyword)
      );
    },
    
    // 按学校分组，每个学校只显示一次，包含多个选科组合
    groupedSchools() {
      const groups = {};
      
      // 按学校ID分组
      this.filteredSchools.forEach(school => {
        if (!groups[school.schoolId]) {
          groups[school.schoolId] = {
            schoolId: school.schoolId,
            schoolName: school.schoolName,
            logo: school.logo,
            disciplines: [],
            // 读取响应式的选中状态，默认为0
            selectedDiscipline: this.selectedDisciplines[school.schoolId] || 0
          };
        }
        // 添加选科组合
        groups[school.schoolId].disciplines.push({
          disciplineType: school.disciplineType,
          rankRange: school.rankRange,
          minScore: school.minScore,
          maxScore: school.maxScore,
          maxScoreRank: school.maxScoreRank,
          minScoreRank: school.minScoreRank
        });
      });
      
      // 返回分组后的数组
      return Object.values(groups);
    },
    
    // 分页后的学校列表
    paginatedSchools() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.groupedSchools.slice(start, end);
    },
    
    // 总页数
    totalPages() {
      return Math.ceil(this.groupedSchools.length / this.pageSize);
    }
  },
  mounted() {},
  methods: {
    // 选科组合切换
    selectDiscipline(group, index) {
      // 更新响应式数据，触发重新渲染
      this.selectedDisciplines[group.schoolId] = index;
    },
    
    // 获取当前选中的选科数据
    currentDiscipline(disciplines, selectedIndex) {
      return disciplines[selectedIndex] || disciplines[0] || {
        disciplineType: '',
        rankRange: '--',
        minScore: null,
        maxScore: null,
        maxScoreRank: null,
        minScoreRank: null
      };
    },
    
    // 获取Logo完整URL
    getLogoUrl(logoPath) {
      if (!logoPath) return '';
      if (logoPath.startsWith('http')) return logoPath;
      return `${baseURL}${logoPath}`;
    },
    
    // 图片加载失败处理
    handleImageError(event) {
      event.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Ctext y=".9em" font-size="90"%3E🏛️%3C/text%3E%3C/svg%3E';
    },
    
    // 格式化分数（去除小数）
    formatScore(score) {
      if (score === null || score === undefined || score === '') return '--';
      return Number(score).toFixed(0);
    },
    
    // 格式化数字
    formatNumber(num) {
      if (num === null || num === undefined || num === '') return '--';
      return Number(num).toFixed(0);
    },
    
    // 切换页面
    handlePageChange(page) {
      this.currentPage = page;
      // 滚动到院校列表顶部
      this.$nextTick(() => {
        const schoolsCard = document.querySelector('.schools-card');
        if (schoolsCard) {
          schoolsCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    
    // 上一页
    handlePrevPage() {
      if (this.currentPage > 1) {
        this.handlePageChange(this.currentPage - 1);
      }
    },
    
    // 下一页
    handleNextPage() {
      if (this.currentPage < this.totalPages) {
        this.handlePageChange(this.currentPage + 1);
      }
    },
    
    // 查询位次和推荐学校
    handleQuery() {
      if (!this.score || this.score < 0 || this.score > 750) {
        this.$message.warning('请输入有效的分数（0-750分）');
        return;
      }
      
      this.isLoading = true;
      
      // 构建请求参数
      const params = {
        score: String(this.score),
        years: this.selectedYear,
        type: this.category === 'physics' ? '0' : '1'
      };
      
      getRankQuery(params).then(response => {
        if (response.data.code === 200) {
          const data = response.data.data;
          
          // 处理位次数据
          this.result = {
            rank: data.rank || 0,
            samescore: data.samescore || 0,
            schoolCount: data.schoolCount || 0,
            isMaxScore: data.isMaxScore || false,
            total: data.total
          };
          
          // 处理推荐学校数据
          if (data.recommendedSchools && Array.isArray(data.recommendedSchools)) {
            this.recommendedSchools = data.recommendedSchools.map(school => ({
              id: school.id,
              schoolId: school.schoolId,
              schoolName: school.schoolName || '未知院校',
              logo: school.logo,
              disciplineType: school.disciplineType || '',
              rankRange: school.rankRange || '--',
              minScore: school.minScore,
              maxScore: school.maxScore,
              maxScoreRank: school.maxScoreRank,
              minScoreRank: school.minScoreRank
            }));
          } else {
            this.recommendedSchools = [];
          }
          
          this.showResult = true;
        } else {
          // 检查是否是"未找到该年份的位次信息"错误
          const errorMsg = response.data.msg || '';
          if (errorMsg.includes('未找到该年份') || (errorMsg.includes('年份') && errorMsg.includes('位次'))) {
            // 清空之前的数据
            this.result = {
              rank: 0,
              samescore: 0,
              schoolCount: 0,
              isMaxScore: false,
              total: null
            };
            this.recommendedSchools = [];
            this.showResult = true;
          }
          this.$message.error(errorMsg || '查询失败');
        }
      }).catch(error => {
        console.error('查询位次失败:', error);
        this.$message.error('查询失败，请稍后重试');
      }).finally(() => {
        this.isLoading = false;
      });
      
      // 滚动到结果位置
      this.$nextTick(() => {
        const resultCard = document.querySelector('.result-card');
        if (resultCard) {
          resultCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  },
};
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  margin-top: 60px;
  padding: 10px 20px 40px;
  background: linear-gradient(135deg, #fff3e0 0%, #f0f4f8 100%);
  box-sizing: border-box;
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
}

.page-title {
  font-size: 36px;
  color: #f57c00;
  margin: 0 0 10px 0;
  font-weight: bold;
}

.page-content {
  max-width: 1000px;
  margin: 0 auto;
}

/* 查询卡片 */
.query-card {
  background: white;
  border-radius: 16px;
  padding: 30px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.card-title {
  font-size: 20px;
  color: #333;
  margin: 0 0 24px 0;
  font-weight: 600;
  text-align: center;
}

/* 横向表单行 */
.form-row {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

/* 表单样式 */
.form-group {
  margin-bottom: 24px;
}

.form-group-horizontal {
  flex: 1;
  min-width: 200px;
}

.form-label {
  display: block;
  font-size: 16px;
  color: #333;
  margin-bottom: 10px;
  font-weight: 500;
}

.score-input-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.score-input {
  flex: 1;
  font-size: 18px;
  padding: 12px 16px;
  border-radius: 10px;
  border: 2px solid #e0e0e0;
  transition: all 0.3s;
  outline: none;
}

.score-input:focus {
  border-color: #f57c00;
  box-shadow: 0 0 0 3px rgba(245, 124, 0, 0.1);
}

.unit-label {
  font-size: 18px;
  color: #666;
  font-weight: 500;
}

/* 年份选择 */
.year-select-wrapper {
  display: flex;
  align-items: center;
}

.year-select {
  width: 100%;
  font-size: 16px;
  border-radius: 10px;
}

/* 选科组合选择 */
.subject-select-wrapper {
  display: flex;
  align-items: center;
}

.subject-select {
  width: 100%;
  font-size: 16px;
  border-radius: 10px;
}

/* 查询按钮 */
.query-btn {
  padding: 28px 40px;
  font-size: 20px;
  border-radius: 12px;
  font-weight: 700;
  background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
  border: none;
  transition: all 0.3s;
  flex-shrink: 0;
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.5);
  color: white;
}

.query-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(76, 175, 80, 0.6);
  background: linear-gradient(135deg, #66bb6a 0%, #43a047 100%);
}

.query-btn:active {
  transform: translateY(-1px);
}

/* 结果卡片 */
.result-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.result-title {
  color: #f57c00;
  margin-bottom: 20px;
  font-size: 18px;
}

.result-item {
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
}

/* 横向排列容器 */
.result-row {
  display: flex;
  gap: 12px;
}

.result-row .result-item {
  flex: 1;
  margin-bottom: 0;
}

.result-icon {
  font-size: 28px;
  margin-right: 16px;
}

/* 分数卡片 */
.score-card {
  background: #ffffff;
  border: 2px solid #e0e0e0;
}

.score-card .result-value {
  color: #333;
}

/* 位次卡片 */
.rank-card {
  background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
  color: white;
}

.rank-card .result-value {
  color: #ffffff;
}

.rank-card .result-label {
  color: rgba(255, 255, 255, 0.9);
}

/* 统计卡片 */
.stats-card {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
  color: white;
}

.stats-card .result-value {
  color: #ffffff;
}

.stats-card .result-label {
  color: rgba(255, 255, 255, 0.9);
}

.result-content {
  flex: 1;
}

.rank-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 30px;
}

.rank-left {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.rank-value-wrapper {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.rank-right {
  flex-shrink: 0;
}

.same-score-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  min-width: 90px;
}

.same-score-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 3px;
}

.same-score-value {
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
}

.result-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
}

.result-value {
  font-size: 28px;
  font-weight: bold;
  color: #333;
}

.result-value.highlight {
  font-size: 32px;
}

.result-sub {
  font-size: 18px;
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.8);
}

.result-unit {
  font-size: 16px;
  color: #999;
  margin-top: 2px;
}

.rank-card .result-unit,
.stats-card .result-unit {
  color: rgba(255, 255, 255, 0.8);
}

/* 推荐学校卡片 */
.schools-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.schools-title {
  color: #f57c00;
  margin-bottom: 16px;
}

/* 匹配统计头部 */
.stats-header {
  margin-bottom: 16px;
}

.stats-header .stats-card {
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
  color: white;
}

.stats-header .stats-card .result-icon {
  font-size: 28px;
  margin-right: 16px;
}

.stats-header .stats-card .result-content {
  flex: 1;
}

.stats-header .stats-card .result-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 4px;
}

.stats-header .stats-card .result-value {
  font-size: 24px;
  font-weight: bold;
  color: #ffffff;
}

.stats-header .stats-card .result-sub {
  font-size: 14px;
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.8);
}

/* 分页容器 */
.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 0 8px 0;
  margin-top: 16px;
  border-top: 1px solid #f0f0f0;
}

/* 院校筛选 */
.school-filter {
  margin-bottom: 16px;
}

.search-input {
  width: 100%;
  border-radius: 8px;
}

.schools-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.school-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #fafafa;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  transition: all 0.3s;
}

.school-card:hover {
  background: #fff;
  border-color: #f57c00;
  box-shadow: 0 2px 12px rgba(245, 124, 0, 0.1);
}

.school-logo {
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.school-info {
  flex: 1;
  min-width: 0;
}

.school-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
}

/* 选科组合标签 */
.discipline-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 6px 0 12px 0;
}

.discipline-tag {
  font-size: 12px;
  padding: 4px 10px;
  background: #f5f5f5;
  color: #666;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid #e0e0e0;
}

.discipline-tag:hover {
  background: rgba(245, 124, 0, 0.1);
  color: #f57c00;
  border-color: rgba(245, 124, 0, 0.3);
}

.discipline-tag.active {
  background: linear-gradient(135deg, #f57c00 0%, #ef6c00 100%);
  color: white;
  border-color: #f57c00;
}

.school-details {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 10px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 calc(25% - 12px);
  min-width: 140px;
}

.detail-label {
  font-size: 16px;
  color: #999;
  white-space: nowrap;
}

.detail-value {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.detail-value.highlight {
  color: #f57c00;
  font-weight: 600;
}

/* 空状态 */
.empty-schools {
  text-align: center;
  padding: 32px;
  background: white;
  border-radius: 16px;
  margin-bottom: 24px;
}

.empty-icon {
  font-size: 40px;
  margin-bottom: 10px;
}

.empty-schools p {
  margin: 0 0 6px 0;
  color: #666;
}

.empty-hint {
  font-size: 14px;
  color: #999 !important;
}

/* 使用说明卡片 */
.info-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.info-title {
  font-size: 18px;
  color: #333;
  margin: 0 0 16px 0;
}

.info-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.info-list li {
  display: flex;
  align-items: flex-start;
  padding: 10px 0;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  border-bottom: 1px solid #f0f0f0;
}

.info-list li:last-child {
  border-bottom: none;
}

.info-icon {
  font-size: 18px;
  margin-right: 10px;
  flex-shrink: 0;
}

/* 响应式 */
@media (max-width: 768px) {
  .page-title {
    font-size: 28px;
  }
  
  .page-content {
    max-width: 100%;
  }
  
  .query-card,
  .result-card,
  .schools-card,
  .info-card {
    padding: 20px;
  }
  
  .result-value {
    font-size: 24px;
  }
  
  .result-value.highlight {
    font-size: 26px;
  }
  
  /* 移动端横向排列改为竖向 */
  .result-row {
    flex-direction: column;
  }
  
  .result-row .result-item {
    margin-bottom: 16px;
  }
  
  .school-card {
    flex-direction: column;
  }
  
  .school-logo {
    width: 100%;
    height: 120px;
  }
  
  .school-details {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}
</style>
